import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';

import {
  ADD_TO_PRESCRIPTION,
  BOTH,
  DESIGNATION,
  DURATION,
  EYE,
  FREQUENCY,
  ID,
  LEFT,
  MEDICATION_DURATION_OPTIONS,
  MEDICATION_FREQUENCY_OPTIONS,
  RETAIN_AFTER_EXAMINATION,
  RIGHT,
  SUB_DRUG,
  TAGS,
  useAlerts,
  useMedicalHistoryActions,
} from 'store/medicalHistory';
import { useAsyncState } from 'utils/useAsyncState';
import Button from 'components/Button';
import Select from 'components/SelectNew';
import CheckBox from 'components/CheckboxNew';
import Start from 'assets/svg-icons/Star';
import Close from 'assets/svg-icons/Close';

import { Container, Wrapper, ButtonWrap, InputWrap, IconButton, CloseButton, Text } from './styles';

const Row = ({
  [ID]: id,
  [SUB_DRUG]: drug,
  [EYE]: eye,
  [FREQUENCY]: frequency,
  [DURATION]: duration,
  [ADD_TO_PRESCRIPTION]: addToPrescription,
  [RETAIN_AFTER_EXAMINATION]: retainAfterExamination,
  onRemove,
  refetchTags,
  widths,
  readMode,
}) => {
  const [values, setValues] = useAsyncState({
    [EYE]: eye,
    [FREQUENCY]: frequency,
    [DURATION]: duration,
    [ADD_TO_PRESCRIPTION]: addToPrescription,
    [RETAIN_AFTER_EXAMINATION]: retainAfterExamination,
    isFavorite: Boolean(drug?.[TAGS]?.includes?.('favorite')),
  });
  const { saveMedication, saveDrug } = useMedicalHistoryActions();
  const { action } = useAlerts(saveMedication);
  const { action: drugAction, success, resetAlerts } = useAlerts(saveDrug);

  const onSelectEye = useCallback(
    ({ currentTarget }) => {
      setValues(($) => {
        const { value } = currentTarget;
        const next =
          ($[EYE] === BOTH && value) ||
          (value === LEFT && $[EYE] === RIGHT && BOTH) ||
          (value === RIGHT && $[EYE] === LEFT && BOTH) ||
          value;

        action({ [ID]: id, [EYE]: next });

        return { ...$, [EYE]: next };
      });
    },
    [action, id, setValues]
  );
  const onChangeFrequency = useCallback(
    (val) => {
      setValues(($) => ({ ...$, [FREQUENCY]: val }));
      action({ [ID]: id, [FREQUENCY]: val });
    },
    [action, id, setValues]
  );
  const onChangeDuration = useCallback(
    (val) => {
      setValues(($) => ({ ...$, [DURATION]: val }));
      action({ [ID]: id, [DURATION]: val });
    },
    [action, id, setValues]
  );
  const onChangeAddToPrescription = useCallback(
    (val) => {
      const next = val ? { [ADD_TO_PRESCRIPTION]: val, [RETAIN_AFTER_EXAMINATION]: !val } : { [ADD_TO_PRESCRIPTION]: val };

      setValues(($) => ({ ...$, ...next }));
      action({ [ID]: id, ...next });
    },
    [action, id, setValues]
  );
  const onChangeRetainAfterExamination = useCallback(
    (val) => {
      const next = val ? { [RETAIN_AFTER_EXAMINATION]: val, [ADD_TO_PRESCRIPTION]: !val } : { [RETAIN_AFTER_EXAMINATION]: val };

      setValues(($) => ({ ...$, ...next }));
      action({ [ID]: id, ...next });
    },
    [action, id, setValues]
  );
  const setFavorite = useCallback(() => {
    setValues(($) => {
      const next = !$.isFavorite;

      if (drug[ID])
        drugAction({
          [ID]: drug[ID],
          [TAGS]: next ? [...(drug[TAGS] || []), 'favorite'] : (drug[TAGS] || []).filter((tag) => tag !== 'favorite'),
        });

      return { ...$, isFavorite: next };
    });
  }, [drug, drugAction, setValues]);
  const onDelete = useCallback(() => onRemove(id), [id, onRemove]);

  useEffect(() => {
    if (success) {
      refetchTags();
      resetAlerts();
    }
  }, [refetchTags, resetAlerts, success]);

  return (
    <Container key={id} $error={false}>
      <Wrapper>
        <ButtonWrap width={widths[0]}>
          <Button
            value={RIGHT}
            onClick={onSelectEye}
            isActive={values[EYE] === RIGHT || values[EYE] === BOTH}
            disabled={readMode}
            height="40px"
            title="OD"
            aria-label="OD"
          >
            OD
          </Button>
          <Button
            value={LEFT}
            onClick={onSelectEye}
            isActive={values[EYE] === LEFT || values[EYE] === BOTH}
            disabled={readMode}
            height="40px"
            title="OS"
            aria-label="OS"
          >
            OS
          </Button>
        </ButtonWrap>
        <InputWrap width={widths[1]}>
          <Select
            value={values[FREQUENCY] || ''}
            options={MEDICATION_FREQUENCY_OPTIONS}
            onSelect={onChangeFrequency}
            readMode={readMode}
            mb={0}
          />
        </InputWrap>
        <InputWrap width={widths[2]}>
          <Select
            value={values[DURATION] || ''}
            options={MEDICATION_DURATION_OPTIONS}
            onSelect={onChangeDuration}
            readMode={readMode}
            mb={0}
          />
        </InputWrap>
        <InputWrap width={widths[3]}>
          <CheckBox
            selected={Boolean(values[ADD_TO_PRESCRIPTION])}
            onClick={onChangeAddToPrescription}
            readMode={readMode}
            mb={0}
          />
        </InputWrap>
        <InputWrap width={widths[4]}>
          <CheckBox
            selected={Boolean(values[RETAIN_AFTER_EXAMINATION])}
            onClick={onChangeRetainAfterExamination}
            readMode={readMode}
            mb={0}
          />
          {!readMode && (
            <CloseButton type="button" onClick={onDelete} disabled={readMode} title="Delete" aria-label="Delete">
              <Close />
            </CloseButton>
          )}
        </InputWrap>
      </Wrapper>
      {Boolean(drug?.[DESIGNATION]) && <Text>{drug[DESIGNATION]}</Text>}
      <IconButton type="button" onClick={setFavorite} disabled={readMode} title="Add to favorite" aria-label="Add to favorite">
        <Start isActive={values.isFavorite} />
      </IconButton>
    </Container>
  );
};

Row.defaultProps = {
  [SUB_DRUG]: null,
  [EYE]: null,
  [FREQUENCY]: '',
  [DURATION]: '',
  [ADD_TO_PRESCRIPTION]: false,
  [RETAIN_AFTER_EXAMINATION]: false,
};
Row.propTypes = {
  [ID]: PropTypes.string.isRequired,
  [SUB_DRUG]: PropTypes.shape({ [DESIGNATION]: PropTypes.string.isRequired }),
  [EYE]: PropTypes.oneOf([LEFT, RIGHT, BOTH]),
  [FREQUENCY]: PropTypes.string,
  [DURATION]: PropTypes.string,
  [ADD_TO_PRESCRIPTION]: PropTypes.bool,
  [RETAIN_AFTER_EXAMINATION]: PropTypes.bool,
  onRemove: PropTypes.func.isRequired,
  refetchTags: PropTypes.func.isRequired,
  widths: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  readMode: PropTypes.bool.isRequired,
};

export default Row;
